/**
 * WebRTC Service
 * 
 * Manages WebRTC peer connections and data channels for the Werewolf game.
 * The Host creates connections to all Participants. Participants connect back to the Host.
 * This creates a star/hub topology with the Host at the center.
 */

export interface WebRTCConfig {
  iceServers?: RTCIceServer[];
}

export interface PeerConnectionInfo {
  peerId: string;
  peerConnection: RTCPeerConnection;
  dataChannel: RTCDataChannel | null;
}

export interface ICECandidateEvent {
  peerId: string;
  candidate: RTCIceCandidateInit;
}

export class WebRTCService {
  private peerConnections: Map<string, PeerConnectionInfo> = new Map();
  private messageHandlers: Set<(peerId: string, data: any) => void> = new Set();
  private messageSentHandlers: Set<(peerId: string, data: any) => void> = new Set();
  private connectionStateHandlers: Set<(peerId: string, state: RTCPeerConnectionState) => void> = new Set();
  private dataChannelHandlers: Set<(peerId: string, channel: RTCDataChannel) => void> = new Set();
  private iceCandidateHandlers: Set<(event: ICECandidateEvent) => void> = new Set();

  private _dataChannelOpenHandlers: Set<(peerId: string, channel: RTCDataChannel) => void> = new Set();
  
  private config: RTCConfiguration;

  constructor(config?: WebRTCConfig) {
    // Use free STUN servers for NAT traversal
    this.config = {
      iceServers: config?.iceServers || [
        { urls: 'stun:stun.l.google.com:19302' },
        { urls: 'stun:stun1.l.google.com:19302' },
      ],
    };
  }

  /**
   * Create a peer connection and generate an offer (used by Host)
   */
  async createOffer(peerId: string): Promise<RTCSessionDescriptionInit> {
    const peerConnection = this.createPeerConnection(peerId);
    
    // Create a data channel
    const dataChannel = peerConnection.createDataChannel('game-data', {
      ordered: true,
    });
    
    this.setupDataChannel(peerId, dataChannel);
    
    // Store the data channel
    const info = this.peerConnections.get(peerId);
    if (info) {
      info.dataChannel = dataChannel;
    }

    // Create offer
    const offer = await peerConnection.createOffer();
    await peerConnection.setLocalDescription(offer);

    return offer;
  }

  /**
   * Handle an incoming offer and create an answer (used by Participant)
   */
  async handleOffer(peerId: string, offer: RTCSessionDescriptionInit): Promise<RTCSessionDescriptionInit> {
    const peerConnection = this.createPeerConnection(peerId);

    await peerConnection.setRemoteDescription(new RTCSessionDescription(offer));
    
    const answer = await peerConnection.createAnswer();
    await peerConnection.setLocalDescription(answer);

    return answer;
  }

  /**
   * Handle an incoming answer (used by Host after sending offer)
   */
  async handleAnswer(peerId: string, answer: RTCSessionDescriptionInit): Promise<void> {
    const info = this.peerConnections.get(peerId);
    if (!info) {
      console.error('No peer connection found for peer:', peerId);
      return;
    }

    await info.peerConnection.setRemoteDescription(new RTCSessionDescription(answer));
  }

  /**
   * Handle an incoming ICE candidate
   */
  async handleIceCandidate(peerId: string, candidate: RTCIceCandidateInit): Promise<void> {
    const info = this.peerConnections.get(peerId);
    if (!info) {
      console.error('No peer connection found for peer:', peerId);
      return;
    }

    await info.peerConnection.addIceCandidate(new RTCIceCandidate(candidate));
  }

  /**
   * Send data to a specific peer
   */
  sendToPeer(peerId: string, data: any): boolean {
    const info = this.peerConnections.get(peerId);
    if (!info || !info.dataChannel || info.dataChannel.readyState !== 'open') {
      console.warn('Cannot send to peer:', peerId, 'Channel not ready');
      return false;
    }

    try {
      info.dataChannel.send(JSON.stringify(data));
      // Notify sent handlers
      this.messageSentHandlers.forEach(handler => {
        handler(peerId, data);
      });
      return true;
    } catch (error) {
      console.error('Error sending data to peer:', peerId, error);
      return false;
    }
  }

  /**
   * Send data to all connected peers
   */
  broadcast(data: any): void {
    this.peerConnections.forEach((info, peerId) => {
      if (info.dataChannel && info.dataChannel.readyState === 'open') {
        this.sendToPeer(peerId, data);
      }
    });
  }

  broadcastExcept(exceptPeerId: string, data: any): void {
    this.peerConnections.forEach((info, peerId) => {
      if (peerId !== exceptPeerId && info.dataChannel && info.dataChannel.readyState === 'open') {
        this.sendToPeer(peerId, data);
      }
    });
  }

  /**
   * Register a handler for incoming messages
   */
  onMessageReceived(handler: (peerId: string, data: any) => void): void {
    this.messageHandlers.add(handler);
  }

  /**
   * Register a handler for sent messages
   */
  onMessageSent(handler: (peerId: string, data: any) => void): void {
    this.messageSentHandlers.add(handler);
  }

  /**
   * Register a handler for connection state changes
   */
  onConnectionStateChange(handler: (peerId: string, state: RTCPeerConnectionState) => void): void {
    this.connectionStateHandlers.add(handler);
  }

  /**
   * Register a handler for data channel creation
   */
  onDataChannel(handler: (peerId: string, channel: RTCDataChannel) => void): void {
    this.dataChannelHandlers.add(handler);
  }

  onDataChannelOpen(handler: (peerId: string, channel: RTCDataChannel) => void): void {
    this._dataChannelOpenHandlers.add(handler);
  }

  /**
   * Register a handler for ICE candidates
   */
  onICECandidate(handler: (event: ICECandidateEvent) => void): void {
    this.iceCandidateHandlers.add(handler);
  }

  /**
   * Close connection to a specific peer
   */
  closePeer(peerId: string): void {
    console.log('Closing peer connection for peer:', peerId);
    const info = this.peerConnections.get(peerId);
    if (info) {
      if (info.dataChannel) {
        info.dataChannel.close();
      }
      info.peerConnection.close();
      this.peerConnections.delete(peerId);
    }
  }

  /**
   * Close all peer connections
   */
  closeAll(): void {
    console.log('Closing all peer connections');
    const peerIds = Array.from(this.peerConnections.keys());
    peerIds.forEach(peerId => this.closePeer(peerId));
    this.peerConnections.clear();
  }

  /**
   * Get the connection state for a peer
   */
  getPeerState(peerId: string): RTCPeerConnectionState | null {
    const info = this.peerConnections.get(peerId);
    return info ? info.peerConnection.connectionState : null;
  }

  /**
   * Get all peer IDs
   */
  getPeerIds(): string[] {
    return Array.from(this.peerConnections.keys());
  }

  /**
   * Creates and configures a new RTCPeerConnection
   */
  private createPeerConnection(peerId: string): RTCPeerConnection {
    const peerConnection = new RTCPeerConnection(this.config);

    // Store the peer connection
    this.peerConnections.set(peerId, {
      peerId,
      peerConnection,
      dataChannel: null,
    });

    // Handle ICE candidates
    peerConnection.onicecandidate = (event) => {
      if (event.candidate) {
        console.log('ICE candidate generated for peer:', peerId);
        const candidate = event.candidate;
        // Notify handlers
        this.iceCandidateHandlers.forEach(handler => {
          handler({ peerId, candidate: candidate.toJSON() });
        });
      }
    };

    // Handle connection state changes
    peerConnection.onconnectionstatechange = () => {
      const state = peerConnection.connectionState;
      console.log('Connection state changed for peer:', peerId, state);
      
      this.connectionStateHandlers.forEach(handler => {
        handler(peerId, state);
      });

      // Clean up if connection fails or closes
      if (state === 'failed' || state === 'closed') {
        this.closePeer(peerId);
      }
    };

    // Handle data channel (for participants receiving from host)
    peerConnection.ondatachannel = (event) => {
      const dataChannel = event.channel;
      console.log('Data channel received for peer:', peerId);
      
      const info = this.peerConnections.get(peerId);
      if (info) {
        info.dataChannel = dataChannel;
      }

      this.setupDataChannel(peerId, dataChannel);

      this.dataChannelHandlers.forEach(handler => {
        handler(peerId, dataChannel);
      });
    };

    return peerConnection;
  }

  /**
   * Sets up event handlers for a data channel
   */
  private setupDataChannel(peerId: string, dataChannel: RTCDataChannel): void {
    dataChannel.onopen = () => {
      console.log('Data channel opened for peer:', peerId);
      this._dataChannelOpenHandlers.forEach(handler => {
        handler(peerId, dataChannel);
      });
    };

    dataChannel.onclose = () => {
      console.log('Data channel closed for peer:', peerId);
    };

    // If the data to send is too large, Chrome simply closes the cannel and
    // an onerror event is fired for the send
    dataChannel.onerror = (error) => {
      console.error(`Data channel error: readyState=${dataChannel.readyState}, peer=${peerId} error=`, error);
    };

    dataChannel.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        this.messageHandlers.forEach(handler => {
          handler(peerId, data);
        });
      } catch (error) {
        console.error('Error parsing message from peer:', peerId, error);
      }
    };
  }
}

export const webRTCService = new WebRTCService();